import { fetchConfig } from '@theme/utilities';

/**
 * "Buy now" button: adds the current variant to the cart, then sends the shopper
 * straight to the plain web checkout (bypassing the cart page and Shopify's
 * dynamic-checkout / Shop Pay buttons, which the theme deliberately hides).
 *
 * This is effectively "add to cart, then go to checkout" — if the shopper
 * abandons checkout the item stays in the cart, same as a normal add.
 */

// Matches ERROR_MESSAGE_DISPLAY_DURATION in product-form.js.
const ERROR_DISPLAY_MS = 10000;
const FALLBACK_ERROR_MESSAGE = 'Unable to add the selected product to the cart.';

/** Locale-aware site root (`/`, `/en/`, …) for building the checkout URL. */
function checkoutRoot() {
  return /** @type {any} */ (window.Shopify).routes.root;
}

class BuyNowButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    clearTimeout(this.errorTimeout);
  }

  handleClick = async () => {
    const form = this.closest('form');
    if (!form || !this.button || this.button.disabled) return;

    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const config = fetchConfig('javascript', { body: new FormData(form) });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'application/json' },
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || !data || data.status) {
        this.#showError(data?.message || data?.description || Theme.translations.add_to_cart_error || FALLBACK_ERROR_MESSAGE);
        this.#reenable();
        return;
      }

      window.location.assign(`${checkoutRoot()}checkout`);
      // Button intentionally left disabled — the page is navigating away.
    } catch (error) {
      console.error(error);
      this.#showError(Theme.translations.add_to_cart_error || FALLBACK_ERROR_MESSAGE);
      this.#reenable();
    }
  };

  #reenable() {
    if (!this.button) return;
    this.button.disabled = false;
    this.button.removeAttribute('aria-busy');
  }

  /**
   * Renders an inline error into the shared product-form error element, mirroring
   * ProductFormComponent#processAddToCart in product-form.js.
   * @param {string} message
   */
  #showError(message) {
    const scope = this.closest('product-form-component') ?? document;
    const errorEl = scope.querySelector('[ref="addToCartTextError"]');
    const liveRegion = scope.querySelector('[ref="liveRegion"]');

    if (errorEl) {
      errorEl.classList.remove('hidden');
      const textNode = errorEl.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        errorEl.appendChild(document.createTextNode(message));
      }

      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => {
        errorEl.classList.add('hidden');
        if (liveRegion) liveRegion.textContent = '';
      }, ERROR_DISPLAY_MS);
    }

    if (liveRegion) liveRegion.textContent = message;
  }
}

if (!customElements.get('buy-now-button')) {
  customElements.define('buy-now-button', BuyNowButton);
}
